// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCompareChanges.test.tsx
//
// SPIKE (side-by-side version compare). The left column while the comparison
// shows its changes: one card per changed window, under the number the window
// carries on both artworks, the selected one opened into a diff -- the window
// cut from either version, removed words tinted red and added ones green by
// warhol, and the edits as diff lines.
import { type AskPunttReviewResolved } from "@mg/schemas/src/christo/askPunttReview";
import { type AskPunttComparisonRegion } from "@mg/schemas/src/christo/versionComparison";
import { Crosshair } from "@phosphor-icons/react";
import { Badge, Button, Checkbox, Text } from "@radix-ui/themes";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { AskV2ReviewCompareSummary } from "./AskV2ReviewCompareSummary";
import { type DiffRow, diffRows, regionTitle } from "./versionChanges";

type AskV2ReviewCompareChangesProps = {
  regions: readonly AskPunttComparisonRegion[];
  /** The window both artworks stand on, by its place in `regions`. */
  selectedIndex: number | null;
  onSelect: (index: number) => void;
  /**
   * Bring both artworks back to a window already selected. why its own
   * callback: selecting the selected window again changes no state, so the
   * viewers would not move.
   */
  onLocate: (region: AskPunttComparisonRegion) => void;
  /** How the older and the newer version are named on screen ("V1", "V2"). */
  beforeMark: string;
  afterMark: string;
  /** Windows a person marked reviewed, by number; absent hides the control. */
  reviewed?: ReadonlySet<number>;
  onToggleReviewed?: (number: number, reviewed: boolean) => void;
  /** The older version's comments each window fixed, by window number. */
  fixes?: ReadonlyMap<number, readonly AskPunttReviewResolved[]>;
  /** Undo Puntt's "fixed" on a comment; absent hides the button. */
  onReopenFix?: (commentId: string) => void;
  reopenPending?: boolean;
  /** Show the fixed comment in the review; absent hides the button. */
  onViewFix?: (commentId: string) => void;
};

// why: one change can fix many comments (8 in one real comparison), and a
// full stack pushed the summary off screen; the rest open on request.
const FIXES_SHOWN = 2;

const ROW_STYLE: Record<DiffRow["kind"], string> = {
  removed: "bg-puntt-red-3 text-puntt-red-11",
  added: "bg-malachite-100 text-malachite-800",
  moved: "bg-puntt-neutral-gray-3 text-puntt-neutral-gray-11",
  context: "text-puntt-neutral-gray-11",
};

const ROW_SIGN: Record<DiffRow["kind"], string> = {
  removed: "−",
  added: "+",
  moved: "↔",
  context: "",
};

function evidenceLabel(
  region: AskPunttComparisonRegion,
  t: (key: string) => string,
): string {
  if (region.evidence === "pixels") return t("askV2.review.compareKindPicture");
  if (region.evidence === "moved") return t("askV2.review.compareKindMoved");
  return t("askV2.review.compareKindWording");
}

function Side({
  region,
  side,
  mark,
}: {
  region: AskPunttComparisonRegion;
  side: "before" | "after";
  mark: string;
}) {
  const { t } = useTranslation("ui");
  const image = region[side];
  const label =
    side === "before"
      ? t("askV2.review.compareBeforeLabel", { mark })
      : t("askV2.review.compareAfterLabel", { mark });
  return (
    <div className="min-w-0 flex-1">
      <div
        className={`flex items-center justify-between px-2 py-1 text-[12px] font-medium ${
          side === "before"
            ? "bg-puntt-red-2 text-puntt-red-11"
            : "bg-malachite-50 text-malachite-700"
        }`}
      >
        <span>
          {side === "before" ? "− " : "+ "}
          {label}
        </span>
        <span>{t("askV2.review.comparePage", { page: region.page })}</span>
      </div>
      {image ? (
        <img
          src={image.source}
          alt={t("askV2.review.compareCutAlt", {
            mark,
            number: region.number,
          })}
          className="block w-full border border-puntt-neutral-gray-4"
          data-testid={`ask-v2-review-compare-change-${side}-${region.number}`}
        />
      ) : (
        <div
          className="flex h-20 items-center justify-center border border-dashed border-puntt-neutral-gray-5 text-[12px] text-puntt-neutral-gray-10"
          data-testid={`ask-v2-review-compare-change-${side}-missing-${region.number}`}
        >
          {t("askV2.review.compareNotInVersion")}
        </div>
      )}
    </div>
  );
}

export function AskV2ReviewCompareChanges({
  regions,
  selectedIndex,
  onSelect,
  onLocate,
  beforeMark,
  afterMark,
  reviewed,
  onToggleReviewed,
  fixes,
  onReopenFix,
  reopenPending = false,
  onViewFix,
}: AskV2ReviewCompareChangesProps) {
  const { t } = useTranslation("ui");
  const selectedRef = useRef<HTMLLIElement | null>(null);
  // The window whose fixed comments are all shown; any other shows the first few.
  const [allFixesFor, setAllFixesFor] = useState<number | null>(null);

  // why: a change stepped to with the arrows or picked on an artwork has to
  // come into view here too, or the column says nothing about it.
  useEffect(() => {
    selectedRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [selectedIndex]);

  return (
    <section
      className="flex min-h-0 flex-1 flex-col overflow-hidden"
      aria-label={t("askV2.review.compareChangesLabel")}
      data-testid="ask-v2-review-compare-changes"
    >
      <ul className="min-h-0 flex-1 space-y-2 overflow-y-auto px-4 py-3">
        {regions.map((region, index) => {
          const open = index === selectedIndex;
          const title = regionTitle(region) ?? evidenceLabel(region, t);
          const rows = diffRows(region);
          const isReviewed = reviewed?.has(region.number) ?? false;
          const fixed = fixes?.get(region.number) ?? [];
          return (
            <li
              key={region.number}
              ref={open ? selectedRef : undefined}
              className={`rounded-lg border bg-base-white ${
                open
                  ? "border-puntt-blue-8 shadow-sm"
                  : "border-puntt-neutral-gray-5"
              }`}
              data-testid={`ask-v2-review-compare-change-card-${region.number}`}
              data-selected={open ? "true" : "false"}
            >
              <div className="flex items-center gap-2 px-3 py-2">
                <button
                  type="button"
                  className="flex min-w-0 flex-1 items-center gap-2 bg-transparent text-left"
                  aria-expanded={open}
                  data-testid={`ask-v2-review-compare-change-open-${region.number}`}
                  onClick={() => onSelect(index)}
                >
                  <span className="font-mono shrink-0 text-[12px] text-puntt-neutral-gray-10">
                    {String(region.number).padStart(2, "0")}
                  </span>
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate text-[14px] font-medium text-puntt-neutral-gray-12">
                      {title}
                    </span>
                    <span
                      className="text-[12px] text-puntt-neutral-gray-11"
                      data-testid={`ask-v2-review-compare-change-meta-${region.number}`}
                    >
                      {evidenceLabel(region, t)} ·{" "}
                      {t("askV2.review.comparePage", { page: region.page })}
                    </span>
                  </span>
                  {fixed.length > 0 ? (
                    <Badge
                      size="1"
                      variant="soft"
                      color="blue"
                      className="shrink-0"
                      data-testid={`ask-v2-review-compare-change-fixes-tag-${region.number}`}
                    >
                      {t("askV2.review.compareFixesTag", {
                        count: fixed.length,
                      })}
                    </Badge>
                  ) : null}
                </button>
                {onToggleReviewed ? (
                  <Checkbox
                    size="2"
                    // why: the round check is the wireframe's reviewed mark;
                    // R does the same from the keyboard.
                    className="shrink-0 [--checkbox-border-radius:9999px]"
                    checked={isReviewed}
                    aria-label={t("askV2.review.compareMarkReviewedLabel", {
                      number: region.number,
                    })}
                    data-testid={`ask-v2-review-compare-change-reviewed-${region.number}`}
                    onCheckedChange={(checked) =>
                      onToggleReviewed(region.number, checked === true)
                    }
                  />
                ) : null}
              </div>
              {open ? (
                <div
                  className="border-t border-puntt-neutral-gray-4 px-3 pb-3 pt-2"
                  data-testid={`ask-v2-review-compare-change-diff-${region.number}`}
                >
                  <div className="flex gap-2">
                    <Side region={region} side="before" mark={beforeMark} />
                    <Side region={region} side="after" mark={afterMark} />
                  </div>
                  <div className="mt-2 flex items-center justify-end text-[12px] text-puntt-neutral-gray-10">
                    <Button
                      variant="ghost"
                      size="1"
                      data-testid={`ask-v2-review-compare-change-locate-${region.number}`}
                      onClick={() => onLocate(region)}
                    >
                      <Crosshair size={14} aria-hidden="true" />
                      {t("askV2.review.compareLocate")}
                    </Button>
                  </div>
                  {rows.length > 0 ? (
                    <ol className="font-mono mt-2 space-y-0.5 text-[12px]">
                      {rows.map((row, rowIndex) => (
                        <li
                          // why the index: one window can list the same words twice.
                          key={`${row.kind}-${rowIndex}`}
                          className={`flex gap-2 rounded px-2 py-0.5 ${ROW_STYLE[row.kind]}`}
                          data-testid={`ask-v2-review-compare-change-row-${region.number}-${rowIndex}`}
                          data-kind={row.kind}
                        >
                          {/* why aria-hidden + sr-only: a name on a role-less span never reaches the tree */}
                          <span className="w-3 shrink-0" aria-hidden="true">
                            {ROW_SIGN[row.kind]}
                          </span>
                          {row.kind === "context" ? null : (
                            <span className="sr-only">
                              {t(`askV2.review.compareRow_${row.kind}`)}:
                            </span>
                          )}
                          <span className="min-w-0 break-words">
                            {row.text}
                          </span>
                        </li>
                      ))}
                    </ol>
                  ) : null}
                  {region.changesMore > 0 ? (
                    <Text as="p" size="1" color="gray" className="mt-1">
                      {t("askV2.review.compareMoreEdits", {
                        count: region.changesMore,
                      })}
                    </Text>
                  ) : null}
                  {region.readByOcr ? (
                    <Text
                      as="p"
                      size="1"
                      color="gray"
                      className="mt-1"
                      data-testid={`ask-v2-review-compare-change-ocr-${region.number}`}
                    >
                      {t("askV2.review.compareReadByOcr")}
                    </Text>
                  ) : null}
                  {fixed.length > 0 ? (
                    <div
                      className="mt-2 flex flex-col gap-2"
                      data-testid={`ask-v2-review-compare-change-fixes-${region.number}`}
                    >
                      {(allFixesFor === region.number
                        ? fixed
                        : fixed.slice(0, FIXES_SHOWN)
                      ).map((entry) => (
                        <div
                          key={entry.commentId}
                          className="flex flex-col gap-2 rounded-md border border-puntt-blue-6 bg-puntt-blue-2 px-3 py-2"
                        >
                          <Text as="p" size="1" weight="bold" color="blue">
                            {t("askV2.review.compareFixesComment", {
                              mark: beforeMark,
                            })}
                          </Text>
                          <Text as="p" size="2">
                            {entry.issue}
                          </Text>
                          {onReopenFix || onViewFix ? (
                            <div className="flex gap-2">
                              {onReopenFix ? (
                                <Button
                                  size="1"
                                  variant="soft"
                                  disabled={reopenPending}
                                  data-testid={`ask-v2-review-compare-change-reopen-${entry.commentId}`}
                                  onClick={() => onReopenFix(entry.commentId)}
                                >
                                  {t("askV2.review.compareReopenComment")}
                                </Button>
                              ) : null}
                              {onViewFix ? (
                                <Button
                                  size="1"
                                  variant="outline"
                                  color="gray"
                                  data-testid={`ask-v2-review-compare-change-view-comment-${entry.commentId}`}
                                  onClick={() => onViewFix(entry.commentId)}
                                >
                                  {t("askV2.review.compareViewComment")}
                                </Button>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      ))}
                      {fixed.length > FIXES_SHOWN &&
                      allFixesFor !== region.number ? (
                        <Button
                          size="1"
                          variant="ghost"
                          className="self-start"
                          data-testid={`ask-v2-review-compare-change-fixes-more-${region.number}`}
                          onClick={() => setAllFixesFor(region.number)}
                        >
                          {t("askV2.review.compareFixesShowMore", {
                            count: fixed.length - FIXES_SHOWN,
                          })}
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
      <AskV2ReviewCompareSummary
        regions={regions}
        fixingCount={
          regions.filter(
            (region) => (fixes?.get(region.number)?.length ?? 0) > 0,
          ).length
        }
      />
    </section>
  );
}
